import React, { useEffect, useState } from 'react';
import { Field, useForm } from 'react-final-form';
import DrillPicker from '../../DrillPicker/DrillPicker';
import { isContactShareBlocked } from '../../../util/chatContactFilter';
import {
  pathsFromListing,
  pathLabel,
  normalizePath,
  legacyTopicForPath,
  isFeaturedPath,
  NOTE_MAX,
  recalledTopicPath,
} from '../../../util/topicPaths';
import css from './TopicsTodayField.module.css';

/**
 * Booking topic step (Sam, Oct 14): ONE drill-down path (any depth) + optional note.
 * Panels read the extras through bookingTopicExtras(listingId) when building orderData.
 */
const store = {};
export const bookingTopicExtras = (listingId, topicsToday = []) => {
  const s = store[listingId] || {};
  const path = normalizePath(s.path);
  const note = (s.note || '').replace(/\s+/g, ' ').trim().slice(0, NOTE_MAX);
  const out = {};
  if (path) {
    out.topicPath = path;
    out.topicPathLabel = pathLabel(path);
    const legacy = legacyTopicForPath(path);
    if (legacy) out.topicsToday = [legacy];
  }
  if (note && !isContactShareBlocked(note)) out.topicNote = note;
  if (!path && topicsToday.length) out.topicsToday = topicsToday;
  return out;
};

export const TopicStep = ({ listing, onLegacyChange, onExtrasChange }) => {
  const id = listing?.id?.uuid || 'listing';
  // Carry the last topic picked or searched on /s (kept for the session, still editable).
  const [carried] = useState(() => (store[id]?.path ? null : recalledTopicPath(false)));
  const [path, setPath] = useState(() => store[id]?.path || carried || null);
  const [note, setNote] = useState(store[id]?.note || '');
  const noteBlocked = note && isContactShareBlocked(note);
  useEffect(() => {
    store[id] = { path, note };
    if (onLegacyChange) {
      const legacy = path ? legacyTopicForPath(path) : null;
      onLegacyChange(legacy ? [legacy] : []);
    }
    if (onExtrasChange) onExtrasChange(bookingTopicExtras(id));
  }, [id, path, note]); // eslint-disable-line react-hooks/exhaustive-deps
  const theirs = pathsFromListing(listing).filter(isFeaturedPath).slice(0, 6);
  return (
    <fieldset className={css.root} data-topics-today="true">
      <legend className={css.legend}>What do you want to talk about? (optional)</legend>
      {path && carried && path === carried ? (
        <p className={css.hint} data-topic-carried="true">
          <strong>Topic:</strong> {pathLabel(path)} <span>(from your search, change it below if you like)</span>
        </p>
      ) : (
        <p className={css.hint}>Pick one topic for this session. Go as specific as you like, or stop at any level.</p>
      )}
      {!path && theirs.length ? (
        <div className={css.theirs} data-topics-today-listener="true">
          <span className={css.theirsLabel}>They love:</span>
          {theirs.map(p => (
            <button key={p} type="button" className={css.theirsChip} onClick={() => setPath(p)}>
              {pathLabel(p)}
            </button>
          ))}
        </div>
      ) : null}
      <DrillPicker
        id="booking-topic"
        limit={1}
        suggest={false}
        value={path ? [path] : []}
        onChange={list => setPath(list[0] || null)}
      />
      <label htmlFor="booking-topic-note" className={css.noteLabel}>
        What’s on your mind? (optional)
      </label>
      <textarea
        id="booking-topic-note"
        className={css.note}
        rows={2}
        maxLength={NOTE_MAX}
        value={note}
        placeholder="e.g. Was that call in the 4th quarter fair?"
        onChange={e => setNote(e.target.value)}
        data-topic-note="true"
      />
      <div className={css.noteMeta}>
        {noteBlocked ? (
          <span className={css.noteError} role="alert">Please leave out phone numbers, emails, links or handles.</span>
        ) : (
          <span />
        )}
        <span>{note.length}/{NOTE_MAX}</span>
      </div>
    </fieldset>
  );
};

/** Final Form field, or controlled when onChange is passed (legacy topicsToday kept in sync). */
const TopicsTodayField = props => {
  const { listing, name = 'topicsToday', onChange } = props;
  if (typeof onChange === 'function') {
    return <TopicStep listing={listing} onLegacyChange={onChange} />;
  }
  return <FormTopicStep listing={listing} name={name} />;
};

/** Inside a Final Form (book-later form): also write topicPath / topicPathLabel / topicNote
 * so they reach orderData (buildCheckoutOrderData keeps unknown keys). */
const EXTRA_KEYS = ['topicPath', 'topicPathLabel', 'topicNote'];
const FormTopicStep = ({ listing, name }) => {
  const form = useForm();
  const onExtrasChange = extras => {
    form.batch(() => {
      EXTRA_KEYS.forEach(k => form.change(k, extras[k] || undefined));
    });
  };
  return (
    <Field name={name}>
      {({ input }) => (
        <TopicStep listing={listing} onLegacyChange={input.onChange} onExtrasChange={onExtrasChange} />
      )}
    </Field>
  );
};

export default TopicsTodayField;
